import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { Modal } from "@material-ui/core";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import useLoader from "../../../hooks/useLoader";
import AuthService from "../../../services/AuthService";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { CloseBtn } from "../../common/MutipleHearings";

/* ─── Animations ──────────────────────────────────────────────────────────── */

const slideUp = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%) scale(0.97); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Action Options ──────────────────────────────────────────────────────── */

const ACTION_OPTIONS = [
  {
    value: 1,
    label: "Generate Meeting Link",
    description: "Create a public-accessible link for this meeting",
    icon: "🔗",
  },
  {
    value: 0,
    label: "Remove Meeting ID",
    description: "Revoke the existing public meeting access",
    icon: "🗑️",
  },
];

/* ─── Component ───────────────────────────────────────────────────────────── */

const GenerateGlobalMeeting = ({
  publicModal,
  setPublicModal,
  id,
  resolutionKind,
  title,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    enqueueSnackbar("Link copied successfully", { variant: "success" });
    setTimeout(() => setCopied(false), 2500);
  };

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "Please wait..." });
      const payload = {
        caseId: values.caseId,
        caseType: values.caseType,
        publicViewer: true,
        title: values.title,
      };

      if (values.typeOfAction) {
        const response = await AuthService.createPublicLink(payload);
        setLink(response?.publicUrl ?? "");
      } else {
        const response = await AuthService.removeToken({
          meetingId: values.caseId,
        });
        resetForm();
        setLink("");
        enqueueSnackbar(response.message, { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <Modal open={publicModal} onClose={() => setPublicModal(false)}>
      <ModalCard role="dialog" aria-modal="true" aria-label="Public Meeting">
        <ModalHeader>
          <HeaderLeft>
            <div>
              <ModalTitle>Public Meeting</ModalTitle>
              {title && <ModalSubtitle>{title}</ModalSubtitle>}
            </div>
          </HeaderLeft>
          <CloseBtn
            onClick={() => setPublicModal(false)}
            aria-label="Close modal"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseBtn>
        </ModalHeader>
        <Formik
          initialValues={{
            caseId: id,
            caseType: resolutionKind || "hearing",
            meetingId: id,
            typeOfAction: 1,
            title: title,
          }}
          enableReinitialize
          onSubmit={onFormSubmit}
        >
          {({ values, handleSubmit, setFieldValue }) => (
            <ModalBody onSubmit={handleSubmit}>
              <SectionLabel>Select Action</SectionLabel>
              <OptionGrid>
                {ACTION_OPTIONS.map((opt) => (
                  <OptionCard
                    key={opt.value}
                    selected={values.typeOfAction === opt.value}
                    destructive={opt.value === 0}
                    onClick={() => setFieldValue("typeOfAction", opt.value)}
                    type="button"
                  >
                    <OptionLabel>{opt.label}</OptionLabel>
                    <OptionDesc>{opt.description}</OptionDesc>
                    <OptionRadio selected={values.typeOfAction === opt.value} />
                  </OptionCard>
                ))}
              </OptionGrid>
              {values.typeOfAction === 1 && link && (
                <LinkContainer>
                  <LinkLabel>Generated Link</LinkLabel>
                  <LinkRow>
                    <LinkInput readOnly value={link} title={link} />
                    <CopyBtn copied={copied} onClick={handleCopy} type="button">
                      {copied ? "✓ Copied" : "Copy"}
                    </CopyBtn>
                  </LinkRow>
                </LinkContainer>
              )}
              {values.typeOfAction === 0 && (
                <WarningBox>
                  <WarningText>
                    This will revoke public access. Anyone with the existing
                    link will no longer be able to join.
                  </WarningText>
                </WarningBox>
              )}
              <ModalFooter>
                <PrimaryBtn
                  type="submit"
                  onClick={handleSubmit}
                  destructive={values.typeOfAction === 0}
                >
                  {values.typeOfAction === 1
                    ? "Generate Link"
                    : "Remove Access"}
                </PrimaryBtn>
              </ModalFooter>
            </ModalBody>
          )}
        </Formik>
      </ModalCard>
    </Modal>
  );
};

export default GenerateGlobalMeeting;

const ModalCard = styled.div`
  max-width: 480px;
  width: calc(100% - 32px);
  background: #ffffff;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.14);
  animation: ${slideUp} 0.24s cubic-bezier(0.4, 0, 0.2, 1) both;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: linear-gradient(
    135deg,
    rgba(0, 131, 140, 0.04) 0%,
    transparent 100%
  );
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-family: ${theme.fonts.primaryFontExtraBold};
  color: ${COLORS.COLOR_DARK};
  letter-spacing: 0.01em;
`;

const ModalSubtitle = styled.p`
  margin: 2px 0 0;
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.INPUT_LABEL};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
`;

const ModalBody = styled.form`
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.INPUT_LABEL};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const OptionCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 14px 14px 12px;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);

  ${({ selected, destructive }) =>
    selected
      ? css`
          border: 1.5px solid ${destructive ? "#EF4444" : COLORS.BTN_GREEN};
          background: ${destructive
            ? "rgba(239,68,68,0.05)"
            : "rgba(0,131,140,0.06)"};
          box-shadow: 0 2px 8px
            ${destructive ? "rgba(239,68,68,0.1)" : "rgba(0,131,140,0.12)"};
        `
      : css`
          border: 1.5px solid rgba(0, 0, 0, 0.08);
          background: #fafafa;
          &:hover {
            border-color: rgba(0, 0, 0, 0.16);
            background: #f4f4f5;
          }
        `}
`;

const OptionLabel = styled.div`
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  line-height: 1.3;
  margin-bottom: 3px;
`;

const OptionDesc = styled.div`
  font-size: 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.INPUT_LABEL};
  line-height: 1.4;
`;

const OptionRadio = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1.5px solid
    ${({ selected }) => (selected ? COLORS.BTN_GREEN : "rgba(0,0,0,0.2)")};
  background: ${({ selected }) =>
    selected ? COLORS.BTN_GREEN : "transparent"};
  transition: all 0.15s ease;

  &::after {
    content: "";
    display: ${({ selected }) => (selected ? "block" : "none")};
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #fff;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const LinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  animation: ${fadeIn} 0.2s ease both;
`;

const LinkLabel = styled.div`
  font-size: 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.INPUT_LABEL};
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const LinkRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LinkInput = styled.input`
  flex: 1;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #f8f9fa;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  outline: none;
  cursor: default;
  min-width: 0;
`;

const CopyBtn = styled.button`
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.17s ease;

  ${({ copied }) =>
    copied
      ? css`
          background: rgba(0, 131, 140, 0.12);
          color: ${COLORS.BTN_GREEN};
        `
      : css`
          background: ${COLORS.BTN_GREEN};
          color: #fff;
          &:hover {
            opacity: 0.88;
          }
          &:active {
            transform: scale(0.97);
          }
        `}
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.06);
  border: 1px solid rgba(239, 68, 68, 0.2);
  animation: ${fadeIn} 0.2s ease both;
`;

const WarningText = styled.p`
  margin: 0;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #b91c1c;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 4px;
`;

const PrimaryBtn = styled.button`
  height: 40px;
  padding: 0 22px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  transition: all 0.17s ease;
  color: #fff;

  background: ${({ destructive }) =>
    destructive ? "#EF4444" : COLORS.BTN_GREEN};

  &:active {
    transform: scale(0.97);
  }
`;
